import { useEffect, useState } from 'react'
import type { ProjectContextTerm } from '@ai-meeting/shared'
import { XMarkIcon } from '@/components/Icons'
import { useUiStrings } from '@/hooks/useUiStrings'

type Props = {
  term: ProjectContextTerm | null
  disabled?: boolean
  onClose: () => void
  onSubmit: (term: ProjectContextTerm) => Promise<void>
}

/**
 * Edit a single declared dictionary term (source / target).
 */
export default function EditDictionaryTermModal({
  term,
  disabled = false,
  onClose,
  onSubmit,
}: Props) {
  const { t } = useUiStrings()
  const [source, setSource] = useState('')
  const [target, setTarget] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!term) return
    setSource(term.source)
    setTarget(term.target ?? '')
  }, [term])

  if (!term) return null

  async function handleSubmit() {
    const trimmedSource = source.trim()
    if (!trimmedSource || !term) return
    setSubmitting(true)
    try {
      const next: ProjectContextTerm = {
        source: trimmedSource,
        targetLang: term.targetLang,
        status: 'ACTIVE',
        ...(target.trim() ? { target: target.trim() } : {}),
      }
      await onSubmit(next)
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-dictionary-term-title"
      data-testid="edit-dictionary-term-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 px-4"
    >
      <div className="flex w-full max-w-md flex-col rounded-2xl bg-[var(--bg-secondary)] shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-[var(--border-color)] px-5 py-4">
          <div>
            <h2
              id="edit-dictionary-term-title"
              className="text-lg font-bold text-[var(--text-primary)]"
            >
              {t.projectDetailDictEditTermTitle}
            </h2>
            <p className="mt-1 text-xs text-[var(--text-tertiary)]">
              {t.projectDetailDictEditTermHint}
            </p>
          </div>
          <button
            type="button"
            aria-label={t.projectDetailDictCloseModal}
            disabled={disabled || submitting}
            onClick={onClose}
            className="rounded-lg p-1 text-[var(--text-tertiary)] hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)]"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-col gap-3 px-5 py-4">
          <label className="flex flex-col gap-1 text-[11px] font-medium text-[var(--text-secondary)]">
            <span>
              {t.projectDetailDictTermSource} <span className="text-[var(--error)]">*</span>
            </span>
            <input
              value={source}
              onChange={(e) => setSource(e.target.value)}
              disabled={disabled || submitting}
              className="input-field h-9 rounded-lg px-3 text-xs"
            />
          </label>
          <label className="flex flex-col gap-1 text-[11px] font-medium text-[var(--text-secondary)]">
            <span>
              {t.projectDetailDictTermTarget} <span className="text-[var(--error)]">*</span>
            </span>
            <input
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              disabled={disabled || submitting}
              className="input-field h-9 rounded-lg px-3 text-xs"
            />
          </label>
        </div>

        <div className="flex justify-end gap-2 border-t border-[var(--border-color)] px-5 py-4">
          <button
            type="button"
            disabled={disabled || submitting}
            onClick={onClose}
            className="rounded-lg border border-[var(--border-color)] px-4 py-2 text-xs font-semibold text-[var(--text-secondary)]"
          >
            {t.projectDetailContextCancel}
          </button>
          <button
            type="button"
            disabled={disabled || submitting || !source.trim()}
            onClick={() => void handleSubmit()}
            className="btn-primary rounded-lg px-4 py-2 text-xs font-semibold disabled:opacity-50"
          >
            {t.saveChanges}
          </button>
        </div>
      </div>
    </div>
  )
}
